import { LitElement, html, css } from 'lit';
import { styleMap } from 'lit/directives/style-map.js';
import { classMap } from 'lit/directives/class-map.js';

// A color picker with the axes swapped from the usual layout: the field is
// hue across and saturation down, the slider is brightness (Photoshop's
// picker with its B radio selected). Value in and out is a hex string; the
// element sets `value` before dispatching `picker-input` while a handle is
// dragged and `picker-change` when it is released.

const clamp = (n, min, max) => Math.min(max, Math.max(min, n));

const hexToHsv = hex => {
  const n = parseInt(hex.replace('#', ''), 16);
  const r = ((n >> 16) & 255) / 255;
  const g = ((n >> 8) & 255) / 255;
  const b = (n & 255) / 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const d = max - min;
  let h = 0;
  if (d) {
    if (max === r) h = ((g - b) / d) % 6;
    else if (max === g) h = (b - r) / d + 2;
    else h = (r - g) / d + 4;
    h = (h * 60 + 360) % 360;
  }
  return { h, s: max ? (d / max) * 100 : 0, v: max * 100 };
};

const hsvToHex = ({ h, s, v }) => {
  const sat = s / 100;
  const val = v / 100;
  const c = val * sat;
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
  const m = val - c;
  const [r, g, b] =
    h < 60
      ? [c, x, 0]
      : h < 120
      ? [x, c, 0]
      : h < 180
      ? [0, c, x]
      : h < 240
      ? [0, x, c]
      : h < 300
      ? [x, 0, c]
      : [c, 0, x];
  return (
    '#' +
    [r, g, b]
      .map(channel =>
        Math.round((channel + m) * 255)
          .toString(16)
          .padStart(2, '0')
      )
      .join('')
  );
};

export class HueFieldPicker extends LitElement {
  static get properties() {
    return {
      value: { type: String },
      label: { type: String },
      _hsv: { type: Object },
      _keyboardFocus: { type: Boolean },
    };
  }

  static get styles() {
    return css`
      /* The rem count is a Lit block token; the catalog page is React and
         does not define it, so it falls back to the site's 16. */
      :host {
        --field-height: calc(120rem / var(--default-rem-px-count, 16));
        --slider-height: calc(8rem / var(--default-rem-px-count, 16));
        --handle-size: calc(12rem / var(--default-rem-px-count, 16));
        display: flex;
        flex-direction: column;
        gap: calc(16rem / var(--default-rem-px-count, 16));
        width: 100%;
      }

      .field,
      .slider {
        position: relative;
        width: 100%;
        cursor: crosshair;
        touch-action: none;
        outline: none;
      }

      .field {
        height: var(--field-height);
      }

      .slider {
        height: var(--slider-height);
        border-radius: 100px;
        cursor: pointer;
      }

      .slider::before {
        content: '';
        position: absolute;
        left: 0;
        right: 0;
        top: calc(-16rem / var(--default-rem-px-count, 16));
        bottom: calc(-8rem / var(--default-rem-px-count, 16));
      }

      .handle {
        position: absolute;
        box-sizing: border-box;
        width: var(--handle-size);
        height: var(--handle-size);
        border: 2px solid #ffffff;
        border-radius: 50%;
        /* A soft shadow lifts the ring off pale colors; design asked for
           no dark outline. */
        box-shadow: 0 1px 3px rgba(0, 0, 0, 0.25);
        transform: translate(-50%, -50%);
        pointer-events: none;
      }

      /* The ring is for keyboard focus only; browsers disagree on whether a
         click on a tabindex box counts as focus-visible, so it is decided
         here: a focus that no pointerdown preceded came from the keyboard. */
      .field.is-keyboard:focus,
      .slider.is-keyboard:focus {
        outline: 2px solid var(--block-text-color, #222222);
        outline-offset: 2px;
      }
    `;
  }

  constructor() {
    super();
    this.value = '#ff9932';
    this.label = 'Color';
    this._hsv = hexToHsv(this.value);
    this._emitted = this.value;
    this._keyboardFocus = false;
    this._pointerFocusPending = false;
  }

  onFocus() {
    this._keyboardFocus = !this._pointerFocusPending;
    this._pointerFocusPending = false;
  }

  onBlur() {
    this._keyboardFocus = false;
  }

  willUpdate(changed) {
    // A value set from outside (the hex field, a query string) moves the
    // handles; our own emissions do not round-trip back through here.
    if (changed.has('value') && this.value && this.value !== this._emitted) {
      this._hsv = hexToHsv(this.value);
      this._emitted = this.value;
    }
  }

  set(partial, commit) {
    this._hsv = { ...this._hsv, ...partial };
    this.value = hsvToHex(this._hsv);
    this._emitted = this.value;
    this.dispatchEvent(
      new CustomEvent(commit ? 'picker-change' : 'picker-input', {
        bubbles: true,
        composed: true,
      })
    );
  }

  // Pointer handling: the element under the pointer captures it, so a
  // drag that leaves the box keeps moving the handle. The handle travels
  // inside the box by its own radius, so it is never clipped at an edge;
  // the pointer maps onto that same inset range.
  pointFrom(event) {
    const box = event.currentTarget;
    const rect = box.getBoundingClientRect();
    const inset = box.querySelector('.handle').offsetWidth / 2;
    const span = axis => Math.max(1, rect[axis] - inset * 2);
    return {
      x: clamp((event.clientX - rect.left - inset) / span('width'), 0, 1),
      y: clamp((event.clientY - rect.top - inset) / span('height'), 0, 1),
    };
  }

  onFieldPointer(event) {
    if (event.type === 'pointerdown') {
      this._pointerFocusPending = true;
      event.currentTarget.setPointerCapture(event.pointerId);
    } else if (event.type === 'pointermove' && !event.buttons) {
      return;
    }
    const { x, y } = this.pointFrom(event);
    this.set({ h: x * 360, s: (1 - y) * 100 }, event.type === 'pointerup');
  }

  onSliderPointer(event) {
    if (event.type === 'pointerdown') {
      this._pointerFocusPending = true;
      event.currentTarget.setPointerCapture(event.pointerId);
    } else if (event.type === 'pointermove' && !event.buttons) {
      return;
    }
    const { x } = this.pointFrom(event);
    this.set({ v: x * 100 }, event.type === 'pointerup');
  }

  onFieldKey(event) {
    const step = event.shiftKey ? 10 : 1;
    const { h, s } = this._hsv;
    const moves = {
      ArrowLeft: { h: (h - step + 360) % 360 },
      ArrowRight: { h: (h + step) % 360 },
      ArrowUp: { s: clamp(s + step, 0, 100) },
      ArrowDown: { s: clamp(s - step, 0, 100) },
    };
    if (!moves[event.key]) return;
    event.preventDefault();
    this.set(moves[event.key], true);
  }

  onSliderKey(event) {
    const step = event.shiftKey ? 10 : 1;
    const moves = {
      ArrowLeft: -step,
      ArrowDown: -step,
      ArrowRight: step,
      ArrowUp: step,
      Home: -100,
      End: 100,
    };
    if (moves[event.key] === undefined) return;
    event.preventDefault();
    this.set({ v: clamp(this._hsv.v + moves[event.key], 0, 100) }, true);
  }

  render() {
    const { h, s, v } = this._hsv;
    const dim = 1 - v / 100;
    const fieldStyle = {
      background: [
        `linear-gradient(rgba(0, 0, 0, ${dim}), rgba(0, 0, 0, ${dim}))`,
        'linear-gradient(to bottom, rgba(255, 255, 255, 0), #ffffff)',
        'linear-gradient(to right, #f00, #ff0, #0f0, #0ff, #00f, #f0f, #f00)',
      ].join(', '),
    };
    const sliderStyle = {
      background: `linear-gradient(to right, #000000, ${hsvToHex({
        h,
        s,
        v: 100,
      })})`,
    };
    // Handle centers run from one radius in to one radius short of the
    // far edge.
    const along = fraction =>
      `calc(var(--handle-size) / 2 + (100% - var(--handle-size)) * ${fraction})`;
    const fieldHandle = {
      left: along(h / 360),
      top: along(1 - s / 100),
      background: this.value,
    };
    const sliderHandle = {
      left: along(v / 100),
      top: '50%',
      background: this.value,
    };

    return html`
      <div
        class=${classMap({ field: true, 'is-keyboard': this._keyboardFocus })}
        role="slider"
        tabindex="0"
        aria-label="${this.label}: hue and saturation"
        aria-valuemin="0"
        aria-valuemax="360"
        aria-valuenow=${Math.round(h)}
        aria-valuetext="hue ${Math.round(h)}, saturation ${Math.round(s)}"
        style=${styleMap(fieldStyle)}
        @pointerdown=${this.onFieldPointer}
        @pointermove=${this.onFieldPointer}
        @pointerup=${this.onFieldPointer}
        @keydown=${this.onFieldKey}
        @focus=${this.onFocus}
        @blur=${this.onBlur}
      >
        <div class="handle" style=${styleMap(fieldHandle)}></div>
      </div>
      <div
        class=${classMap({ slider: true, 'is-keyboard': this._keyboardFocus })}
        role="slider"
        tabindex="0"
        aria-label="${this.label}: brightness"
        aria-valuemin="0"
        aria-valuemax="100"
        aria-valuenow=${Math.round(v)}
        style=${styleMap(sliderStyle)}
        @pointerdown=${this.onSliderPointer}
        @pointermove=${this.onSliderPointer}
        @pointerup=${this.onSliderPointer}
        @keydown=${this.onSliderKey}
        @focus=${this.onFocus}
        @blur=${this.onBlur}
      >
        <div class="handle" style=${styleMap(sliderHandle)}></div>
      </div>
    `;
  }
}

customElements.define('hue-field-picker', HueFieldPicker);
